import React from 'react';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import { HeartPulse, CheckCircle } from 'lucide-react';
export default function TraumaPTSD() {
  return <main className="min-h-screen bg-white" data-id="element-1289">
      <Helmet data-id="element-1290">
        <title data-id="element-1291">
          PTSD & Trauma Psychiatric Evaluations | Medico-Legal Psychiatry
        </title>
        <meta name="description" content="Expert PTSD & Trauma Psychiatric Evaluations by Dr. Imtiaz Syed. Independent assessments for personal injury, asylum cases and legal proceedings across the UK." data-id="element-1292" />
        <meta name="keywords" content="PTSD & Trauma Psychiatric Evaluations, PTSD Evaluations, Trauma Assessment, Complex PTSD, Istanbul Protocol, Psychological Injury, Expert Witness Psychiatrist, Independent PTSD assessment for asylum cases UK" data-id="element-1293" />
        <script type="application/ld+json" data-id="element-1294">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "PTSD & Trauma Psychiatric Evaluations",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "PTSD & Trauma Evaluation",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert PTSD & Trauma Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 pt-24 md:pt-28" data-id="element-1295">
        <div className="container mx-auto px-4" data-id="element-1296">
          <div className="flex items-center justify-center mb-6" data-id="element-1297">
            <HeartPulse className="h-16 w-16 mr-4" data-id="element-1298" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-1299">
              PTSD & Trauma Psychiatric Evaluations
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1300">
            Expert Insights into Psychological Injury
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-16" data-id="element-1301">
        <div className="container mx-auto px-4" data-id="element-1302">
          <div className="max-w-3xl mx-auto" data-id="element-1303">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1304">
              Understanding the profound impact of trauma is crucial in many
              legal contexts. Dr. Imtiaz Syed provides meticulous and sensitive
              assessments for Post-Traumatic Stress Disorder (PTSD) and Complex
              PTSD, offering clear and robust expert opinions for legal
              proceedings. His evaluations help illuminate the psychological
              consequences of traumatic events, ensuring these are accurately
              presented and understood within the legal framework.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1305">
              Why Instruct Dr. Imtiaz Syed for Trauma & PTSD Evaluations?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1306">
              Dr. Syed's expertise in trauma-informed psychiatry ensures:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1307">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1308">
                <div className="flex items-start" data-id="element-1309">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1310" />
                  <div data-id="element-1311">
                    <h3 className="font-bold text-primary" data-id="element-1312">
                      Sensitive & Thorough Assessments
                    </h3>
                    <p className="text-slate" data-id="element-1313">
                      Conducted with deep understanding and respect for the
                      individual's experience.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1314">
                <div className="flex items-start" data-id="element-1315">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1316" />
                  <div data-id="element-1317">
                    <h3 className="font-bold text-primary" data-id="element-1318">
                      Istanbul Protocol Compliance
                    </h3>
                    <p className="text-slate" data-id="element-1319">
                      Reports for international contexts (e.g., asylum) often
                      adhere to these rigorous standards.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1320">
                <div className="flex items-start" data-id="element-1321">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1322" />
                  <div data-id="element-1323">
                    <h3 className="font-bold text-primary" data-id="element-1324">
                      Clarifying Complex Trauma
                    </h3>
                    <p className="text-slate" data-id="element-1325">
                      Expertise in assessing conditions arising from torture,
                      gender-based violence (GBV), modern slavery, and other
                      severe experiences.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1326">
                <div className="flex items-start" data-id="element-1327">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1328" />
                  <div data-id="element-1329">
                    <h3 className="font-bold text-primary" data-id="element-1330">
                      Objective & Robust Reports
                    </h3>
                    <p className="text-slate" data-id="element-1331">
                      Providing impartial, evidence-based findings that clearly
                      outline diagnosis, causation, and prognosis where
                      relevant.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1332">
              Key Trauma & PTSD Assessments Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1333">
              Dr. Syed offers expert evaluations for:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1334">
              <li className="flex items-start" data-id="element-1335">
                <span className="text-secondary font-bold mr-2" data-id="element-1336">•</span>
                <span data-id="element-1337">
                  <strong data-id="element-1338">Post-Traumatic Stress Disorder (PTSD):</strong>{' '}
                  Diagnostic assessments for the classic presentation of PTSD.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1339">
                <span className="text-secondary font-bold mr-2" data-id="element-1340">•</span>
                <span data-id="element-1341">
                  <strong data-id="element-1342">Complex PTSD:</strong> Evaluations for prolonged,
                  repeated, or multiple forms of trauma.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1343">
                <span className="text-secondary font-bold mr-2" data-id="element-1344">•</span>
                <span data-id="element-1345">
                  <strong data-id="element-1346">Trauma-Related Psychological Injuries:</strong>{' '}
                  Assessments in personal injury claims where trauma has led to
                  psychological distress.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1347">
                <span className="text-secondary font-bold mr-2" data-id="element-1348">•</span>
                <span data-id="element-1349">
                  <strong data-id="element-1350">Impact of Specific Traumatic Events:</strong> Expert
                  opinions on the psychological effects of accidents, abuse, or
                  other significant incidents.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1351">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1352">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed offers both secure remote assessments across the UK and
              in-person consultations when appropriate. The resulting reports
              are always clear, legally relevant, and provide precise findings
              and actionable insights for your case, designed to withstand
              scrutiny.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1353">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1354">
              Dr. Imtiaz Syed is your trusted partner in navigating the
              complexities of trauma-related claims. His expert evaluations
              provide the clarity and depth required to ensure psychological
              injuries are understood and justly addressed within the legal
              system.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1355">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1356">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1357">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1358">
                <span className="mr-2 text-slate-dark" data-id="element-1359">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1360">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1361">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1362">
                Book a PTSD Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}